// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
// @ts-nocheck
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Badge,
  Loader,
  Center,
  Table,
  Alert,
} from '@mantine/core';
import {
  IconDownload,
  IconReportAnalytics,
  IconRefresh,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { fileDownloadUrl } from '../../utils/format';
import { formatDateTime } from '../../utils/format';
import { useReportResult } from '../../hooks/useReportResult';

interface ReportColumn {
  key: string;
  label: string;
}

interface ReportSummary {
  key: string;
  label: string;
  value: string;
}

interface ReportResult {
  id: string;
  name: string;
  reportType: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  downloadUrl: string | null;
  parameters: Record<string, unknown> | null;
  summary: ReportSummary[];
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
};

export default function ReportResultPage() {
  const { id } = useParams<{ id: string }>();

  const { data: queryData, loading: isLoading, refetch, startPolling, stopPolling } = useReportResult(id || '');
  const reportData = queryData as { reportResult?: ReportResult } | undefined;
  const report = reportData?.reportResult;

  // Poll while processing
  if (report?.status === 'pending' || report?.status === 'processing') {
    startPolling(3000);
  } else {
    stopPolling();
  }

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!report) {
    return (
      <Stack gap="md">
        <PageHeader
          title="Report Not Found"
          breadcrumbs={[
            { label: 'Reports', to: '/admin/workforce/reports' },
            { label: 'Not Found' },
          ]}
        />
        <EmptyState
          icon={<IconReportAnalytics size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="Report Not Found"
          message="The requested report could not be found."
        />
      </Stack>
    );
  }

  const isProcessing = report.status === 'pending' || report.status === 'processing';
  const isFailed = report.status === 'failed';

  return (
    <Stack gap="md">
      <PageHeader
        title={report.name}
        breadcrumbs={[
          { label: 'Reports', to: '/admin/workforce/reports' },
          { label: report.name },
        ]}
        actions={
          <Group gap="sm">
            {isProcessing && (
              <Button
                variant="light"
                icon={<IconRefresh size={16} />}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            )}
            {report.downloadUrl && (
              <Button
                icon={<IconDownload size={16} />}
                component="a"
                href={fileDownloadUrl(report.downloadUrl)}
                target="_blank"
              >
                Download
              </Button>
            )}
          </Group>
        }
      />

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <div>
            <Text size="sm" c="dimmed">
              Report Type
            </Text>
            <Text fw={500}>
              {report.reportType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Badge color={STATUS_COLORS[report.status] || 'gray'} size="lg">
              {report.status.toUpperCase()}
            </Badge>
          </div>
        </Group>

        <Group gap="xl">
          <div>
            <Text size="xs" c="dimmed">
              Created
            </Text>
            <Text size="sm">{formatDateTime(report.createdAt)}</Text>
          </div>
          {report.completedAt && (
            <div>
              <Text size="xs" c="dimmed">
                Completed
              </Text>
              <Text size="sm">{formatDateTime(report.completedAt)}</Text>
            </div>
          )}
        </Group>
      </Paper>

      {isProcessing && (
        <Alert icon={<IconRefresh size={16} />} color="blue" title="Processing">
          This report is still being generated. It will auto-refresh every 3 seconds.
        </Alert>
      )}

      {isFailed && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Failed">
          This report failed to generate. Please try again.
        </Alert>
      )}

      {report.summary.length > 0 && (
        <Paper withBorder p="lg" radius="md">
          <Text fw={600} size="lg" mb="md">
            Summary
          </Text>
          <Group gap="xl" wrap="wrap">
            {report.summary.map((item) => (
              <div key={item.key}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  {item.label}
                </Text>
                <Text fw={700} fz={24}>
                  {item.value}
                </Text>
              </div>
            ))}
          </Group>
        </Paper>
      )}

      {report.columns.length > 0 && report.rows.length > 0 && (
        <Paper withBorder p="lg" radius="md">
          <Group justify="space-between" mb="md">
            <Text fw={600} size="lg">
              Data ({report.rows.length} rows)
            </Text>
          </Group>

          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                {report.columns.map((col) => (
                  <Table.Th key={col.key}>{col.label}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {report.rows.map((row, idx) => (
                <Table.Tr key={idx}>
                  {report.columns.map((col) => (
                    <Table.Td key={col.key}>
                      <Text size="sm">
                        {row[col.key] != null ? String(row[col.key]) : '-'}
                      </Text>
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
